import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useAuth from "common/hooks/useAuth";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import { isPasswordCorrect } from "common/components/Password/Password";
import useRouter from "common/hooks/use-router";
import EditHeader from "common/components/EditHeader/EditHeader";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import { Organization } from "types/Organization";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import RadioBlockItem from "common/components/RadioBlockItem/RadioBlockItem";
import rolesData, { roleNames, superRolesData } from "conf/roleTypesData";
import useOrganization from "common/hooks/useOrganization";
import getWording from "common/utils/wording";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import imgSizes from "conf/imgSizes.json";
import { checkEmail, checkPhoneFormat } from "common/utils/fieldsValidators";
import { Constants } from "constants/Constants";
import OrganizationSelect from "components/select/OrganizationSelect/OrganizationSelect";
import ProviderSelect from "components/select/ProviderSelect/ProviderSelect";

function UserPublicForm({ onSubmit, editedUser, myAccount }: any) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { organization } = useOrganization();
  const { navigate } = useRouter();
  const [errorMessage, setErrorMessage] = useState("");
  const [changePassword, setChangePassword] = useState(false);
  const [displayPassword, setDisplayPassword] = useState(true);

  const initialValues = useMemo(
    () =>
      editedUser?.id
        ? {
            ...editedUser,
            role: editedUser?.role?.id ?? 3,
            organizations: editedUser?.organizations?.map(
              (o: Organization) => o.id,
            ),
            role_name: editedUser?.role?.name,
          }
        : {
            organizations: organization ? [organization?.id] : [],
            name: undefined,
            password: undefined,
            role: 3,
            email: "",
            phoneNumber: undefined,
            blocked: false,
            confirmed: true,
            siteUsers: [],
            followedSites: [],
            avatar: null,
            notificationSettings: {
              postNew: true,
              threadNew: true,
              onlinePost: true,
              siteUpdate: true,
              validatePost: true,
              contactUpdate: true,
              voteInPostNew: true,
              activityUpdate: true,
              commentInPostNew: true,
              contactGroupUpdate: true,
              publicMessageInThreadNew: true,
              privateMessageInThreadNew: true,
              assignedOnOnly: false,
            },
          },
    [editedUser, organization],
  );

  const checkPassword = (value: string) =>
    isPasswordCorrect(value) ? undefined : t("common.passwordTooWeak");

  const validateForm = (values: any) => {
    if (values.password !== values.confirmPassword) {
      return { confirmPassword: t("common.resetPasswordError") };
    }
    if (
      Boolean(
        values?.role_name === "Administrateur" || values?.role_name === "Pro",
      ) &&
      values.organizations?.length === 0
    ) {
      return { organizations: t("common.fieldRequired") };
    }
    return {};
  };

  const onSubmitWrapper = async (values: any) => {
    const valuesCopy = { ...values };
    if (values.role === 1) {
      // remove notificationSettings if (finally) the user is not a BO user.
      delete valuesCopy.notificationSettings;
    }
    const res = await onSubmit(valuesCopy);

    if (res.message) {
      setErrorMessage(res.message);
    } else {
      setChangePassword(false);
    }
  };

  const handleProviderChange = (form: any, value?: string) => {
    if (!value || value === "local") {
      setDisplayPassword(true);
      // vider les champs si on repasse en mode "local"
      form.change("password", "");
      form.change("confirmPassword", "");
    } else {
      setDisplayPassword(false);
      // remplir avec une valeur aléatoire identique
      const randomValue = Math.random().toString(36).slice(2, 12);
      form.change("password", randomValue);
      form.change("confirmPassword", randomValue);
    }
  };

  const displayRoleChoice =
    !myAccount &&
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        !["SuperAdmin", "Public", "Authenticated"].includes(
          initialValues.role_name,
        )));

  const displaySuperAdminRole =
    currentUser?.isSuperadmin && !!Constants.IS_MULTI_ORGANIZATIONS;

  const setDefaultNotificationSettings = () => {
    if (!initialValues.notificationSettings) {
      initialValues.notificationSettings = {
        postNew: true,
        commentInPostNew: true,
        voteInPostNew: true,
        validatePost: true,
        onlinePost: true,
        threadNew: true,
        privateMessageInThreadNew: true,
        publicMessageInThreadNew: true,
        assignedOnOnly: true,
        activityUpdate: true,
        siteUpdate: true,
        contactUpdate: true,
        contactGroupUpdate: true,
      };
    }
  };

  console.log("je suis la");

  return (
    <Form
      onSubmit={onSubmitWrapper}
      mutators={{
        ...arrayMutators,
      }}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      validate={validateForm}
      render={({ handleSubmit, values, form }) => (
        <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
          {/* Header */}
          <EditHeader
            title={
              myAccount
                ? `${t("titlePages.myAccount")}`
                : t(`common.${editedUser?.id ? "edit" : "add"}`)
            }
            subtitle={values.name}
            icon="UsersIcon"
            errorMessage={errorMessage}
            HeaderButtonsEdit={
              <HeaderButtonsEdit
                inHeader
                onSubmit={handleSubmit}
                onCancel={myAccount ? null : () => navigate("/public-accounts")}
              />
            }
          />
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FormScrollError />

            <div className="responsive-flex gap-2 mt-2">
              <div className="md:w-2/3 flex flex-col gap-2">
                <div className="white-box">
                  {displayRoleChoice && (
                    <>
                      <SectionTitle
                        icon="CalendarDaysIcon"
                        title={t("forms.role")}
                      />
                      <FormControl
                        type="custom"
                        name="role"
                        inputContainerStyle="flex gap-3 mb-4"
                        containerStyle="w-full"
                        // editable={currentUser.role.name === "SuperAdmin"}
                        onChange={(newValue) => {
                          form.change("role_name", roleNames[newValue]);
                          setDefaultNotificationSettings();
                          return newValue;
                        }}
                        editable={
                          !["Authenticated", "Pro"].includes(
                            currentUser.role.name,
                          )
                        }
                        // editable={initialValues.role_name !== "Authenticated"}
                      >
                        <RadioBlock
                          Component={RadioBlockItem}
                          items={
                            displaySuperAdminRole ? superRolesData : rolesData
                          }
                          // items={
                          //   initialValues.role_name === "Authenticated"
                          //     ? pubRolesData
                          //     : rolesData
                          // }
                          horizontal
                        />
                      </FormControl>
                    </>
                  )}
                  <div className="responsive-flex gap-4">
                    {editedUser?.role?.name !== "Authenticated" && (
                      <div className="flex flex-col w-full md:w-[180px]">
                        <FormControl
                          title={t("forms.avatar")}
                          labelIcon="PhotoIcon"
                          name="avatar"
                          type="image"
                          maxWidth={imgSizes.avatar.width}
                          maxHeight={imgSizes.avatar.width}
                          keepRatio
                        />
                      </div>
                    )}

                    <div className="flex flex-col w-full gap-4">
                      <FormControl
                        name="name"
                        titleKey={t("forms.name")}
                        required
                      />

                      {Constants.ENABLE_LOGIN && (
                        <FormControl
                          type="custom"
                          name="provider"
                          titleKey={t("forms.authenticationProvider")}
                          onChange={(value) =>
                            handleProviderChange(form, value)
                          }
                        >
                          <ProviderSelect />
                        </FormControl>
                      )}

                      {editedUser?.id ? (
                        values?.provider !== "local" ? (
                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-slate-700">
                              {t("forms.authenticationProvider")}
                            </label>
                            <div className="text-base text-slate-900 capitalize">
                              {values.provider}
                            </div>
                            <p className="text-sm text-slate-500">
                              {t("forms.passwordManagedByProvider")}
                            </p>
                          </div>
                        ) : (
                          <>
                            <ToggleSwitch
                              rightLabel={t("forms.newPassword")}
                              value={changePassword}
                              onChange={setChangePassword}
                            />
                            {changePassword && (
                              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                <FormControl
                                  type="jpassword"
                                  name="password"
                                  titleKey={t("forms.password")}
                                  helpText={t("common.passwordInstruction")}
                                  required
                                  validate={checkPassword}
                                />
                                <FormControl
                                  name="confirmPassword"
                                  title={t("forms.confirmPassword")}
                                  type="password"
                                />
                              </div>
                            )}
                          </>
                        )
                      ) : (
                        displayPassword && (
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            <FormControl
                              type="jpassword"
                              name="password"
                              titleKey={t("forms.password")}
                              helpText={t("common.passwordInstruction")}
                              required
                              validate={checkPassword}
                            />
                            <FormControl
                              name="confirmPassword"
                              title={t("forms.confirmPassword")}
                              type="password"
                            />
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {(currentUser?.isAdmin || currentUser?.isSuperadmin) &&
                  (values?.role_name === "SuperAdmin" ||
                    values?.role_name === "Administrateur" ||
                    values?.role_name === "Pro") && (
                    <div className="white-box">
                      <SectionTitle
                        title={t("forms.notificationsSettings")}
                        className="mb-4 text-lg normal-case"
                      />
                      <div className="grid-1or2 xl- gap-2">
                        <div className="flex w-full flex-col gap-4">
                          <SectionTitle
                            title={t("titlePages.posts")}
                            className="mb-1"
                          />
                          <FormControl
                            type="custom"
                            name="notificationSettings.postNew"
                          >
                            <ToggleSwitch
                              rightLabel={t("notificationSettings.postNew")}
                            />
                          </FormControl>

                          <FormControl
                            type="custom"
                            name="notificationSettings.commentInPostNew"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.commentInPostNew",
                              )}
                            />
                          </FormControl>

                          <FormControl
                            type="custom"
                            name="notificationSettings.voteInPostNew"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.voteInPostNew",
                              )}
                            />
                          </FormControl>

                          <FormControl
                            type="custom"
                            name="notificationSettings.validatePost"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.validatePost",
                              )}
                            />
                          </FormControl>

                          <FormControl
                            type="custom"
                            name="notificationSettings.onlinePost"
                          >
                            <ToggleSwitch
                              rightLabel={t("notificationSettings.onlinePost")}
                            />
                          </FormControl>
                        </div>
                        <div className="flex w-full flex-col gap-4">
                          <SectionTitle
                            title={t("titlePages.threads")}
                            className="mb-1 mt-4 xl:mt-0"
                          />

                          <FormControl
                            type="custom"
                            name="notificationSettings.threadNew"
                          >
                            <ToggleSwitch
                              rightLabel={t("notificationSettings.threadNew")}
                            />
                          </FormControl>

                          <FormControl
                            type="custom"
                            name="notificationSettings.privateMessageInThreadNew"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.privateMessageInThreadNew",
                              )}
                            />
                          </FormControl>

                          <FormControl
                            type="custom"
                            name="notificationSettings.publicMessageInThreadNew"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.publicMessageInThreadNew",
                              )}
                            />
                          </FormControl>

                          <FormControl
                            type="custom"
                            name="notificationSettings.assignedOnOnly"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.assignedOnOnly",
                              )}
                            />
                          </FormControl>
                        </div>
                        <div className="flex w-full flex-col gap-4 mt-4">
                          <SectionTitle
                            title={t(
                              "notificationSettings.notificationsForUpdates",
                            )}
                            className="mb-1"
                          />
                          <FormControl
                            type="custom"
                            name="notificationSettings.activityUpdate"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.activityUpdate",
                              )}
                            />
                          </FormControl>
                          <FormControl
                            type="custom"
                            name="notificationSettings.siteUpdate"
                          >
                            <ToggleSwitch
                              rightLabel={t("notificationSettings.siteUpdate", {
                                wording: getWording(),
                              })}
                            />
                          </FormControl>
                          <FormControl
                            type="custom"
                            name="notificationSettings.contactUpdate"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.contactUpdate",
                              )}
                            />
                          </FormControl>
                          <FormControl
                            type="custom"
                            name="notificationSettings.contactGroupUpdate"
                          >
                            <ToggleSwitch
                              rightLabel={t(
                                "notificationSettings.contactGroupUpdate",
                              )}
                            />
                          </FormControl>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
              <div className="w-full md:w-1/3">
                <div className="white-box">
                  <div className="w-full gap-2 flex flex-col">
                    {(currentUser?.isAdmin || currentUser?.isSuperadmin) && (
                      <FormControl type="custom" name="confirmed">
                        <ToggleSwitch
                          rightLabel={
                            values.confirmed
                              ? t("forms.confirmed")
                              : t("forms.unconfirmed")
                          }
                        />
                      </FormControl>
                    )}
                    {Boolean(
                      (currentUser?.isAdmin || currentUser?.isSuperadmin) &&
                        (values?.role_name === "SuperAdmin" ||
                          values?.role_name === "Administrateur" ||
                          values?.role_name === "Pro"),
                    ) && (
                      <FormControl
                        type="custom"
                        name="organizations"
                        titleKey={t(
                          Constants.IS_MULTI_ORGANIZATIONS
                            ? "forms.organizations"
                            : "forms.organization",
                        )}
                        multiple={Constants.IS_MULTI_ORGANIZATIONS}
                        required
                      >
                        <OrganizationSelect />
                      </FormControl>
                    )}
                    <SectionTitle
                      title={t("forms.coordinates")}
                      className="text-lg normal-case mt-4"
                    />
                    <FormControl
                      name="email"
                      titleKey={t("forms.email")}
                      required
                      validate={(val: string) => checkEmail(val, t)}
                    />
                    <FormControl
                      name="phoneNumber"
                      titleKey={t("forms.phoneNumber")}
                      validate={(val: string) =>
                        !val ? undefined : checkPhoneFormat(val, t)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <HeaderButtonsEdit
              onSubmit={handleSubmit}
              onCancel={myAccount ? null : () => navigate("/public-accounts")}
            />
          </form>
        </div>
      )}
    />
  );
}
export default UserPublicForm;
